import is from '@sindresorhus/is';
import {
    createContext,
    type FunctionComponent,
    type PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '~/ui/core/context-menu/ContextMenu';
import { IconChatBubble7, IconCopy, IconCut, IconPaste } from '~/ui/core/icons';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import { useCopyTextToClipboard } from '../use-copy-text-to-clipboard';
import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { type CommentHighlightState, commentHighlightClassName } from './comment-highlight';
import { TitleCommentRowContext, type TitleCommentRowContextValue } from './title-comment-row-context';
import {
    getInputSelectionRange,
    hasInputSelection,
    insertIntoInput,
    removeInputSelection,
} from './title-input-clipboard';
import { useActivateThread } from './use-activate-thread';
import { useCommentableState } from './use-commentable-state';

type TitleThreadState = {
    readonly hasComment: boolean;
    readonly highlightClassName: string;
    readonly isActive: boolean;
    readonly state: CommentHighlightState;
};

const resolveTitleHighlightClassName = (state: CommentHighlightState): string =>
    state === 'none' ? '' : commentHighlightClassName(state);

const readRowTextSelection = (row: HTMLElement | null): string | null => {
    if (is.null(row)) return null;
    const selection = window.getSelection();
    if (is.null(selection) || selection.isCollapsed || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!row.contains(range.startContainer) || !row.contains(range.endContainer)) return null;
    const text = range.toString();
    return is.emptyStringOrWhitespace(text) ? null : text;
};

const TitleThreadActiveContext = createContext<TitleThreadState>({
    hasComment: false,
    highlightClassName: '',
    isActive: false,
    state: 'none',
});

export const useIsTitleThreadActive = (): boolean => useContext(TitleThreadActiveContext).isActive;

export const useTitleThreadState = (): TitleThreadState => useContext(TitleThreadActiveContext);

type CommentableTitleProps = PropsWithChildren<{
    readonly itemId: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly title: string;
}>;

export const CommentableTitle: FunctionComponent<CommentableTitleProps> = ({
    children,
    itemId,
    newThreadCreationDisabled = false,
    title,
}) => {
    const translate = useTranslate();
    const rowRef = useRef<HTMLDivElement | null>(null);
    const titleInputRef = useRef<HTMLInputElement | null>(null);
    const copyTextToClipboard = useCopyTextToClipboard();
    const { showErrorMessageSnackbar } = useSnackbars();
    const setPendingTarget = useActiveThreadStore((state) => state.setPendingTarget);
    const setIsCommentsOpen = useActiveThreadStore((state) => state.setIsCommentsOpen);
    const activateThread = useActivateThread(itemId);
    const { state, commentId, savedComment } = useCommentableState({ kind: 'title', itemId });
    const [isEditing, setIsEditing] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);

    const handleOpenChange = useCallback((open: boolean): void => {
        if (!open) return;
        const input = titleInputRef.current;
        setIsEditing(!is.null(input));
        setHasSelection(!is.null(input) && hasInputSelection(input));
    }, []);

    const handleRowClick = useCallback((): void => {
        if (!is.undefined(savedComment)) {
            setIsCommentsOpen(true);
            activateThread(savedComment.id, savedComment.target);
        }
    }, [savedComment, setIsCommentsOpen, activateThread]);

    const handleComment = useCallback((): void => {
        setIsCommentsOpen(true);
        if (!is.undefined(savedComment)) {
            activateThread(savedComment.id, savedComment.target);
            return;
        }
        const rowEl = rowRef.current?.querySelector<HTMLElement>('[data-anchor-kind="title"]') ?? null;
        setPendingTarget({
            target: { kind: 'title' },
            anchorRect: rowEl?.getBoundingClientRect() ?? new DOMRect(),
            anchorEl: rowEl,
            commentId: crypto.randomUUID(),
            prevDetails: null,
        });
    }, [savedComment, setIsCommentsOpen, setPendingTarget, activateThread]);

    const handleCopy = useCallback((): void => {
        const input = titleInputRef.current;
        if (is.null(input)) {
            const selectedText = readRowTextSelection(rowRef.current);
            void copyTextToClipboard(is.null(selectedText) ? title : selectedText);
            return;
        }
        const { start, end } = getInputSelectionRange(input);
        void copyTextToClipboard(hasInputSelection(input) ? input.value.slice(start, end) : input.value);
    }, [copyTextToClipboard, title]);

    const handleCut = useCallback((): void => {
        const input = titleInputRef.current;
        if (is.null(input) || !hasInputSelection(input)) return;
        const { start, end } = getInputSelectionRange(input);
        const selected = input.value.slice(start, end);
        void copyTextToClipboard(selected).then((copied) => {
            if (copied) removeInputSelection(input);
        });
    }, [copyTextToClipboard]);

    const handlePaste = useCallback((): void => {
        const input = titleInputRef.current;
        if (is.null(input)) return;
        if (is.nullOrUndefined(navigator.clipboard)) {
            showErrorMessageSnackbar('pages.job-architecture-v2.comments.paste-error');
            return;
        }
        navigator.clipboard.readText().then(
            (text) => {
                insertIntoInput(input, text);
            },
            () => {
                showErrorMessageSnackbar('pages.job-architecture-v2.comments.paste-error');
            },
        );
    }, [showErrorMessageSnackbar]);

    const rowValue = useMemo<TitleCommentRowContextValue>(() => {
        const rowDataAttrs: Record<string, string> = {
            'data-anchor-kind': 'title',
            'data-item-id': itemId,
            'data-testid': 'commentable-title',
        };
        if (!is.undefined(commentId)) {
            rowDataAttrs['data-comment-id'] = commentId;
        }
        return {
            rowDataAttrs,
            onRowClick: is.undefined(savedComment) ? undefined : handleRowClick,
            titleInputRef,
        };
    }, [savedComment, commentId, itemId, handleRowClick]);

    const providedThreadState = useMemo<TitleThreadState>(
        () => ({
            hasComment: state === 'saved' || state === 'active',
            highlightClassName: resolveTitleHighlightClassName(state),
            isActive: state === 'active',
            state,
        }),
        [state],
    );

    return (
        <TitleCommentRowContext.Provider value={rowValue}>
            <TitleThreadActiveContext.Provider value={providedThreadState}>
                <ContextMenu onOpenChange={handleOpenChange}>
                    <ContextMenuTrigger>
                        <div ref={rowRef} className="contents">
                            {children}
                        </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="!min-w-[7rem]" onCloseAutoFocus={(event) => event.preventDefault()}>
                        {!newThreadCreationDisabled && (
                            <ContextMenuItem onSelect={handleComment}>
                                <IconChatBubble7 size={12} />
                                {translate('pages.job-architecture-v2.comments.selection-button.label')}
                            </ContextMenuItem>
                        )}
                        {isEditing ? (
                            <>
                                {!newThreadCreationDisabled && <ContextMenuSeparator />}
                                <ContextMenuItem disabled={!hasSelection} onSelect={handleCut}>
                                    <IconCut size={12} />
                                    {translate('common.cut')}
                                </ContextMenuItem>
                                <ContextMenuItem onSelect={handleCopy}>
                                    <IconCopy size={12} />
                                    {translate('common.copy')}
                                </ContextMenuItem>
                                <ContextMenuItem onSelect={handlePaste}>
                                    <IconPaste size={12} />
                                    {translate('common.paste')}
                                </ContextMenuItem>
                            </>
                        ) : (
                            <ContextMenuItem onSelect={handleCopy}>
                                <IconCopy size={12} />
                                {translate('common.copy')}
                            </ContextMenuItem>
                        )}
                    </ContextMenuContent>
                </ContextMenu>
            </TitleThreadActiveContext.Provider>
        </TitleCommentRowContext.Provider>
    );
};
